import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, MessageCircle, Phone, Send } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Reveal";
import { API, PHONE_OFFICE, WHATSAPP_URL } from "../lib/api";

const PRESETS = [
  "Voglio vendere senza annunci pubblici",
  "Cerco un immobile da acquistare",
  "Ho un capitale da investire in immobili",
  "Cosa include il protocollo AC START?",
];

const newId = () =>
  window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function AssistenteSection() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Benvenuto in A.C. HUB. Sono l'assistente della direzione: chiedimi come funziona un protocollo, quale fa al caso tuo o come iniziare il tuo percorso immobiliare.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(newId());
  const listRef = useRef(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const stream = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (!delta) continue;
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + delta };
              return copy;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          text: "Connessione non riuscita in questo momento. Puoi chiamarci direttamente al 351 4129152 o scrivere su WhatsApp: rispondiamo subito.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="assistente" className="section" data-testid="assistente-section">
      <div className="container-hub grid items-center gap-14 lg:grid-cols-2">
        <div>
          <SectionHeading
            eyebrow="Assistente A.C. HUB"
            title={<>UN ASSISTENTE IMMOBILIARE. <span className="gold-text">24 ORE SU 24.</span></>}
            lead="L'assistente conosce i cinque protocolli, i servizi e il metodo A.C. HUB: risponde davvero, in italiano, con il tono della consulenza riservata. Provalo subito."
          />
          <Reveal delay={0.25}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={PHONE_OFFICE} data-testid="ai-contact-phone" className="btn btn-outline !min-h-[42px] !px-4 !text-[9px]">
                <Phone size={12} /> 351 4129152
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ai-contact-whatsapp"
                className="btn btn-outline !min-h-[42px] !px-4 !text-[9px]"
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="border border-[#c9a45c]/25 bg-[#101010] p-6 sm:p-7" data-testid="ai-chat-shell">
            <div className="flex items-center justify-between border-b border-[#c9a45c]/20 pb-4 text-[11px] font-bold tracking-[0.12em]">
              <span className="text-cream">ASSISTENTE A.C. HUB</span>
              <span className="flex items-center gap-2 text-[9px] tracking-[0.16em] text-gold-light">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#7ec98c]" />
                DISPONIBILE
              </span>
            </div>

            <div ref={listRef} className="flex max-h-[330px] min-h-[280px] flex-col gap-3 overflow-y-auto py-5 pr-1" data-testid="ai-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`chat-msg ${m.role === "user" ? "chat-msg-user" : "self-start"}`}
                  data-testid={m.role === "user" ? "ai-msg-user" : "ai-msg-assistant"}
                >
                  {m.text || (
                    <span className="flex gap-1 py-1">
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {PRESETS.map((q, i) => (
                <button
                  key={q}
                  data-testid={`ai-question-${i + 1}`}
                  onClick={() => stream(q)}
                  disabled={busy}
                  className="flex items-center justify-between gap-2 border border-[#c9a45c]/20 px-3.5 py-2.5 text-left text-[11.5px] text-[#d7d4ce] transition-colors duration-200 hover:border-gold hover:text-gold-light disabled:opacity-50"
                >
                  {q}
                  <ArrowUpRight size={12} className="shrink-0 text-gold" />
                </button>
              ))}
            </div>

            <form
              className="mt-4 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                stream(input);
              }}
            >
              <input
                className="field !py-3"
                placeholder="Scrivi la tua domanda…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="ai-input"
              />
              <button
                type="submit"
                data-testid="ai-send-button"
                disabled={busy || !input.trim()}
                className="btn btn-gold !min-h-[46px] !px-4 disabled:opacity-40"
                aria-label="Invia messaggio"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </form>

            <p className="mt-4 border-t border-[#c9a45c]/16 pt-3 text-[10px] leading-relaxed text-[#8f8d88]" data-testid="ai-disclaimer">
              Assistente AI a scopo informativo: non fornisce consulenza legale, fiscale o finanziaria né promesse
              di rendimento. Per il tuo caso contatta l'HUB.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
